import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionBankService } from '../services/questionBankService';
import { QuestionBank, QuestionBankRequest } from '../types';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, ClipboardIcon } from '../components/Icons';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../context/ToastContext';

const QuestionBanksPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [banks, setBanks] = useState<QuestionBank[]>([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBank, setEditingBank] = useState<QuestionBank | null>(null);
  const [formData, setFormData] = useState<QuestionBankRequest>({ name: '', description: '' });
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; bankId: number | null }>({ show: false, bankId: null });
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchBanks();
  }, [page, keyword]);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const result = await questionBankService.getBanks(keyword || undefined, page, 20);
      setBanks(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingBank(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleEdit = (bank: QuestionBank) => {
    setEditingBank(bank);
    setFormData({ name: bank.name, description: bank.description || '' });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showToast('请输入题库名称', 'error');
      return;
    }
    try {
      if (editingBank) {
        await questionBankService.updateBank(editingBank.id, formData);
        showToast('题库更新成功', 'success');
      } else {
        await questionBankService.createBank(formData);
        showToast('题库创建成功', 'success');
      }
      setShowModal(false);
      fetchBanks();
    } catch (error: any) {
      showToast(error.response?.data?.message || '操作失败', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.bankId) return;
    try {
      await questionBankService.deleteBank(deleteConfirm.bankId);
      showToast('题库已删除', 'success');
      setDeleteConfirm({ show: false, bankId: null });
      fetchBanks();
    } catch (error: any) {
      showToast(error.response?.data?.message || '删除失败', 'error');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchBanks();
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-on-surface font-headline">题库管理</h1>
          <p className="text-sm text-on-surface-variant font-body mt-1">创建和管理题库，方便题目复用</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal hover:shadow-soft-md"
        >
          <PlusIcon size={20} />
          创建题库
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant" size={20} />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索题库..."
            className="w-full pl-10 pr-4 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </form>

      {/* Bank List */}
      {banks.length === 0 && !loading ? (
        <div className="text-center py-12 bg-surface-container-lowest rounded-3xl shadow-soft">
          <ClipboardIcon className="mx-auto mb-4 text-on-surface-variant" size={48} />
          <h3 className="text-lg font-medium text-on-surface font-headline mb-2">暂无题库</h3>
          <p className="text-on-surface-variant font-body mb-4">创建一个题库来管理可复用的题目</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl shadow-teal hover:shadow-soft-md"
          >
            <PlusIcon size={20} />
            创建题库
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {banks.map((bank) => (
            <div
              key={bank.id}
              className="bg-surface-container-lowest p-5 rounded-3xl shadow-soft hover:shadow-soft-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/question-banks/${bank.id}`)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ClipboardIcon className="text-primary-600" size={20} />
                  <h3 className="text-lg font-medium text-on-surface font-headline">{bank.name}</h3>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleEdit(bank)}
                    className="p-1.5 text-on-surface-variant hover:text-primary-600 transition-colors"
                    title="编辑"
                  >
                    <EditIcon size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ show: true, bankId: bank.id })}
                    className="p-1.5 text-on-surface-variant hover:text-danger-600 transition-colors"
                    title="删除"
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              </div>
              {bank.description && (
                <p className="text-sm text-on-surface-variant font-body mb-3 line-clamp-2">{bank.description}</p>
              )}
              <div className="flex items-center justify-between text-sm text-on-surface-variant font-label">
                <span>{bank.questionCount} 道题目</span>
                <span>{new Date(bank.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 bg-surface-container-high rounded-xl text-sm disabled:opacity-50 font-label"
          >
            上一页
          </button>
          <span className="px-3 py-1 text-sm text-on-surface-variant font-label">
            {page + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 bg-surface-container-high rounded-xl text-sm disabled:opacity-50 font-label"
          >
            下一页
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl p-6 w-full max-w-md shadow-soft">
            <h3 className="text-lg font-semibold text-on-surface font-headline mb-4">
              {editingBank ? '编辑题库' : '创建题库'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-on-surface font-label mb-1">题库名称 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                  placeholder="请输入题库名称"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface font-label mb-1">描述</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                  rows={3}
                  placeholder="请输入题库描述（可选）"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-on-surface bg-surface-container-high rounded-xl hover:bg-surface-container-low"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm gradient-primary text-on-primary rounded-xl shadow-teal hover:shadow-soft-md"
              >
                {editingBank ? '保存' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="删除题库"
        message="确定要删除此题库吗？题库中的所有题目也将被删除。"
        confirmText="删除"
        cancelText="取消"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ show: false, bankId: null })}
        type="danger"
      />
    </div>
  );
};

export default QuestionBanksPage;
